import type { ConnectorOauthExtraConfigProps } from "@app/lib/connector_providers_ui";
import { getPKCEConfig } from "@app/lib/utils/pkce";
import {
  isValidClientIdOrSecret,
  isValidSalesforceDomain,
} from "@app/types/oauth/lib";
import { Input } from "@dust-tt/sparkle";
import { useLingui } from "@lingui/react/macro";
import { useEffect } from "react";

export function SalesforceOauthExtraConfig({
  extraConfig,
  setExtraConfig,
  setIsExtraConfigValid,
}: ConnectorOauthExtraConfigProps) {
  const { t } = useLingui();

  useEffect(() => {
    async function generatePKCE() {
      const { code_verifier, code_challenge } = await getPKCEConfig();
      setExtraConfig((extraConfig) => ({
        ...extraConfig,
        code_verifier,
        code_challenge,
      }));
    }

    void generatePKCE();
  }, [extraConfig.instance_url, setExtraConfig]);

  useEffect(() => {
    setIsExtraConfigValid(
      !!extraConfig.instance_url &&
        isValidSalesforceDomain(extraConfig.instance_url) &&
        !!extraConfig.client_id &&
        isValidClientIdOrSecret(extraConfig.client_id) &&
        !!extraConfig.client_secret &&
        isValidClientIdOrSecret(extraConfig.client_secret) &&
        !!extraConfig.code_verifier &&
        !!extraConfig.code_challenge
    );
  }, [extraConfig, setIsExtraConfigValid]);

  const isErrorUrl =
    typeof extraConfig.instance_url === "string" &&
    extraConfig.instance_url.length > 0 &&
    !isValidSalesforceDomain(extraConfig.instance_url);

  return (
    <>
      <Input
        label={t`Salesforce instance URL`}
        message={t`Must be a valid Salesforce domain in https and ending with .salesforce.com`}
        name="instance_url"
        value={extraConfig.instance_url ?? ""}
        placeholder="https://my-org.salesforce.com"
        onChange={(e) => {
          setExtraConfig((prev: Record<string, string>) => ({
            ...prev,
            instance_url: e.target.value,
          }));
        }}
        messageStatus={isErrorUrl ? "error" : "default"}
      />
      <Input
        label={t`Client ID`}
        message={t`The client ID from your Salesforce connected app.`}
        name="client_id"
        value={extraConfig.client_id ?? ""}
        placeholder="3MVG9..."
        onChange={(e) => {
          setExtraConfig((prev: Record<string, string>) => ({
            ...prev,
            client_id: e.target.value,
          }));
        }}
      />
      <Input
        label={t`Client secret`}
        message={t`The client secret from your Salesforce connected app.`}
        name="client_secret"
        value={extraConfig.client_secret ?? ""}
        placeholder="..."
        type="password"
        onChange={(e) => {
          setExtraConfig((prev: Record<string, string>) => ({
            ...prev,
            client_secret: e.target.value,
          }));
        }}
      />
    </>
  );
}
